import * as React from "react"
import { ACCESSIBLY_HIDDEN } from "@digits-shared/components/UI/Elements/Form/InputStyles"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled, { css } from "styled-components"

export const COMMON_INPUT_FOCUS_STYLES = css`
  border: 1px solid ${colors.accentBlue};
  outline: 1px solid ${colors.accentBlue};
  background: ${colors.white};
`

export const COMMON_INPUT_HOVER_STYLES = css`
  border: 1px solid ${colors.secondary50};
  background: ${colors.white};
`

export const COMMON_INPUT_STYLES = css<{ $invalid?: boolean }>`
  display: block;
  color: ${colors.secondary};
  font-family: ${fonts.family.avenir};
  font-size: 14px;

  appearance: none;
  background: ${colors.translucentWhite50};
  padding: 6px 8px;
  width: -moz-available;
  width: -webkit-fill-available;
  width: fill-available;

  cursor: text;
  outline: none;

  border: 1px solid ${colors.secondary30};
  border-radius: 8px;

  transition: border 0.2s ease-in-out;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &::placeholder {
    font-family: ${fonts.family.avenir};
    color: ${colors.secondary50};
  }

  &[disabled] {
    color: ${colors.secondary50};
    background: ${colors.secondary05};
    pointer-events: none;
  }

  &:invalid:not(:placeholder-shown):not(:focus):not(:hover) {
    border: 1px solid ${colors.errorDark};
  }

  &:focus {
    ${COMMON_INPUT_FOCUS_STYLES};
  }

  &:hover:not([disabled]),
  &:hover:not(:focus-within):not(:invalid):not([disabled]),
  &:hover:not(:focus-within):placeholder-shown:invalid:not([disabled]) {
    ${COMMON_INPUT_HOVER_STYLES};
  }

  ${({ $invalid }) =>
    $invalid &&
    css`
      &:not(:focus):not([data-state="open"]) {
        border: 1px solid ${colors.errorDark};
      }
    `}
`

export const GHOST_INPUT_STYLES = css<{ $invalid?: boolean }>`
  ${COMMON_INPUT_STYLES};

  transition: background 0.2s ease-in-out;
  background: ${colors.transparent};
  border: 1px solid ${colors.transparent};

  &[disabled] {
    background: ${colors.transparent};
  }

  &:hover:not(:focus-within):not(:invalid),
  &:hover:not(:focus-within):placeholder-shown:invalid {
    border-color: ${colors.transparent};
    background: ${colors.secondary05};
  }
`

export const InnerInput = styled.input<{ $invalid?: boolean }>`
  ${COMMON_INPUT_STYLES};
  ${({ $invalid }) =>
    $invalid &&
    css`
      border: 1px solid ${colors.errorDark};
    `}
`

export const InputContainer = styled.div<{ $small: boolean; fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  gap: 2px;
  min-width: 0;

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}

  ${InnerInput} {
    height: ${({ $small }) => ($small ? "32px" : "40px")};
  }
`

export type InputLabelProps = { $invalid?: boolean; $hide?: boolean }

export const INPUT_LABEL_STYLES = css<InputLabelProps>`
  ${fonts.scale.detail};
  display: flex;
  align-items: center;
  gap: 4px;
  ${({ $invalid }) =>
    $invalid &&
    css`
      color: ${colors.errorDark};
    `}
  ${({ $hide }) =>
    $hide &&
    css`
      ${ACCESSIBLY_HIDDEN};
    `}
`

export const InputLabel = styled.label<InputLabelProps>`
  ${INPUT_LABEL_STYLES}
`

export const HintText = styled.span`
  ${fonts.scale.detail};
  color: ${colors.secondary60};
`

export const HelpText = styled.span`
  ${fonts.scale.detail};
  color: ${colors.secondary60};
`

export const ErrorText = styled.span`
  position: absolute;
  bottom: -16px;
  left: 0;
  ${fonts.scale.detail};
  color: ${colors.errorDark};
`

const OptionalSpan = styled.span`
  color: ${colors.secondary60};
`

export type InputProps = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  errorText?: string
  fullWidth?: boolean
  hintText?: string
  helpText?: string
  label?: React.ReactNode
  optional?: boolean
  hideLabel?: boolean
  small?: boolean
  invalid?: boolean
  inputClassName?: string
}

export const Input: React.FC<React.PropsWithChildren<InputProps>> = React.forwardRef(
  (
    {
      className,
      label,
      optional = false,
      hideLabel,
      errorText,
      hintText,
      helpText,
      fullWidth = false,
      small = false,
      invalid,
      inputClassName,
      children,
      ...rest
    },
    ref
  ) => {
    const showErrorText = Boolean(invalid && errorText)
    return (
      <InputContainer className={className} $small={small} fullWidth={fullWidth}>
        {label && (
          <InputLabel htmlFor={rest.id} $invalid={invalid} $hide={hideLabel}>
            <Label label={label} optional={optional} />
            {!!hintText && <HintText>{hintText}</HintText>}
          </InputLabel>
        )}
        <InnerInput {...rest} className={inputClassName} ref={ref} $invalid={invalid} />
        {showErrorText && <ErrorText>{errorText}</ErrorText>}
        {helpText && !showErrorText && <HelpText>{helpText}</HelpText>}
        {children}
      </InputContainer>
    )
  }
)

const Label: React.FC<{ label: InputProps["label"]; optional: boolean }> = ({ label, optional }) =>
  optional ? (
    <>
      {label}
      <OptionalSpan>(Optional)</OptionalSpan>
    </>
  ) : (
    label
  )
